import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiHelpers, extractApiData } from '@/api/core';
import type {
  CreateSubscriptionRequest,
  CompanySubscriptionEntry,
  SubscriptionStatusResponse,
} from '@packages/types/company';
import { invalidateCompanyRelatedQueries } from '@/api/cache/organizationMutationCache';

const subscriptionKeys = {
  all: ['company-subscription'] as const,
  detail: (companyId: number) => [...subscriptionKeys.all, companyId] as const,
};

// 구독 현황 + 이력 조회 (Admin)
export const useGetSubscription = (
  companyId: number,
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: subscriptionKeys.detail(companyId),
    queryFn: async () => {
      const response = await apiHelpers.get<SubscriptionStatusResponse>(
        'company.getSubscription',
        { id: companyId }
      );
      return extractApiData(response);
    },
    enabled: !!companyId && options?.enabled !== false,
    ...options,
  });
};

// 구독 등록/갱신 (Admin)
export const useCreateSubscription = (companyId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateSubscriptionRequest) => {
      const response = await apiHelpers.post<CompanySubscriptionEntry>(
        'company.createSubscription',
        data,
        { id: companyId }
      );
      return extractApiData(response);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: subscriptionKeys.detail(companyId),
      });
      invalidateCompanyRelatedQueries(queryClient);
    },
  });
};

// 구독 즉시 취소 (Admin)
export const useCancelSubscription = (companyId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const response = await apiHelpers.patch<null>(
        'company.cancelSubscription',
        undefined,
        { id: companyId }
      );
      return extractApiData(response);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: subscriptionKeys.detail(companyId),
      });
      invalidateCompanyRelatedQueries(queryClient);
    },
  });
};
